import { useTranslation } from 'react-i18next';
import BreadcrumbsNav from 'components/BreadcrumbsNav/BreadcrumbsNav';
import Button from 'components/Button/Button';
import CustomPagination from 'components/CustomPagination/CustomPagination';
import DeactivationModal from 'components/DeactivationModals/DeactivationModal';
import Loader from 'components/Loader/Loader';
import Panel from 'components/Panel/Panel';
import { TASK_TABS, TASK_TYPE_ACTION } from 'constants/general';
import {
  BUTTON_VARIANT, DELETE_MODAL_TITLES, USER_PILLAR_ACCESS_TYPES, USERS_ROLE,
} from 'constants/interfaces';
import { TASK_STATUS_REQUIRED_COMMENT } from 'constants/taskPriority';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import CreateTaskModal from '../TaskManagement/components/CreateTaskModal';
import TaskInfoActivities from './components/TaskInfoActivities/TaskInfoActivities';
import TaskInfoComments from './components/TaskInfoComments/TaskInfoComments';
import TaskInfoDetails from './components/TaskInfoDetails/TaskInfoDetails';
import TaskInfoHeader from './components/TaskInfoHeader/TaskInfoHeader';
import TaskInfoTable from './components/TaskInfoTable/TaskInfoTable';

import type { TTaskStatus } from 'constants/interfaces';
import type ITaskInfo from './interfaces/ITaskInfo';

import styles from './sass/TaskInfo.module.scss';

function TaskInfoView({
  data,
  onStatusChange,
  dataTable,
  breadcrumbs,
  isDeleteModalOpen,
  onDeleteSwimlaneConfirm,
  getPriorityIcon,
  getFileNameWithExtension,
  onGoToTask,
  getStatusClassName,
  control,
  activeTab,
  handleTabChange,
  getUserInitials,
  isLoading,
  attachments,
  isCommentModalVisible,
  onCommentReject,
  onCommentConfirm,
  isCreateTaskModalVisible,
  onCloseModal,
  onOpenCreateTaskModal,
  onOpenDeleteModal,
  onCloseDeleteModal,
  userData,
  activitiesItems,
  onAddComment,
  onDeleteComment,
  isActivitiesListLoading,
  newStatus,
  hideFieldsForm,
  taskId,
  currentPage,
  totalPages,
  onPageChange,
}: ITaskInfo) {
  const { t } = useTranslation();
  const onCheckExistStatus = (status: TTaskStatus) => TASK_STATUS_REQUIRED_COMMENT.includes(status);

  const isAdminOrCompanyAdmin = userData.role === USERS_ROLE.admin || userData.role === USERS_ROLE.companyAdmin;
  const isExistAccess = userData.permissionTaskManagement === USER_PILLAR_ACCESS_TYPES.viewEdit;

  const currentLinkedTask = dataTable.filter((task) => task.id !== Number(taskId));
  return (
    <div className={styles.root}>
      <div className={styles.breadcrumbs}>
        <BreadcrumbsNav breadcrumbs={breadcrumbs} />
      </div>

      {!isLoading ? (
        <Panel className={styles.panel}>
          <TaskInfoHeader
            title={data?.title || ''}
            onEdit={() => onOpenCreateTaskModal(false)}
            onDelete={onOpenDeleteModal}
            canEdit={isAdminOrCompanyAdmin}
          />
          <TaskInfoDetails
            data={data}
            onStatusChange={onStatusChange}
            getPriorityIcon={getPriorityIcon}
            getFileNameWithExtension={getFileNameWithExtension}
            attachments={attachments}
            accessChangeStatus={isExistAccess}
            onOpenCreateTaskModal={onOpenCreateTaskModal}
          />
          {currentLinkedTask.length > 0 ? (
            <TaskInfoTable
              dataTable={currentLinkedTask}
              onGoToTask={onGoToTask}
              getStatusClassName={getStatusClassName}
            />
          ) : null}
          <div className={styles.tabsContainer}>
            <div className={styles.tabsHeader}>
              <Button
                title="Activities"
                variant={BUTTON_VARIANT.ghost}
                className={`${styles.tabButton} ${
                  activeTab === TASK_TABS.ACTIVITIES ? styles.tabButtonActive : ''
                }`}
                onClick={() => handleTabChange(TASK_TABS.ACTIVITIES)}
              />
              <Button
                title="Comments"
                variant={BUTTON_VARIANT.ghost}
                className={`${styles.tabButton} ${
                  activeTab === TASK_TABS.COMMENTS ? styles.tabButtonActive : ''
                }`}
                onClick={() => handleTabChange(TASK_TABS.COMMENTS)}
              />
            </div>
            {!isActivitiesListLoading ? (
              <div className={styles.tabsContent}>
                {activeTab === TASK_TABS.ACTIVITIES && (
                  <TaskInfoActivities
                    activities={activitiesItems || []}
                    getUserInitials={getUserInitials}
                    emptyListText={t('taskManagement.noActivities')}
                  />
                )}
                {activeTab === TASK_TABS.COMMENTS && (
                  <TaskInfoComments
                    comments={activitiesItems || []}
                    control={control}
                    getUserInitials={getUserInitials}
                    onAddComment={onAddComment}
                    userData={userData}
                    onDeleteComment={onDeleteComment}
                    emptyListText={t('taskManagement.noComments')}
                  />
                )}
              </div>
            ) : null}
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </div>
        </Panel>
      ) : (
        <Panel className={styles.panel}>
          <Loader isPageLoading isFullLoader={false} />
        </Panel>
      )}
      <DeleteModal
        open={isDeleteModalOpen}
        onReject={onCloseDeleteModal}
        isLoading={false}
        name={data?.title || ''}
        title={DELETE_MODAL_TITLES.DELETE_TASK}
        onConfirm={onDeleteSwimlaneConfirm}
      />
      <DeactivationModal
        open={isCommentModalVisible}
        title={t('taskManagement.commentModalTitle')}
        text={onCheckExistStatus(newStatus as TTaskStatus)
          ? t('taskManagement.commentRequired') : t('taskManagement.commentOptional')}
        placeholder={t('taskManagement.commentModalDescription')}
        onReject={onCommentReject}
        onSubmit={onCommentConfirm}
        hideLabel
        isLoading={false}
        showInfoIcon
        offValidation={!onCheckExistStatus(newStatus as TTaskStatus)}
      />
      <CreateTaskModal
        open={isCreateTaskModalVisible}
        onClose={onCloseModal}
        typeModal={TASK_TYPE_ACTION.EDIT}
        itemData={data}
        attachments={attachments}
        hideFieldsForm={hideFieldsForm}
        accessDeleteFile={isAdminOrCompanyAdmin}
        currentStatus={data?.status as TTaskStatus}
      />
    </div>
  );
}

export default TaskInfoView;
